import {
  type ChangeEvent,
  type ComponentPropsWithRef,
  useCallback,
  useRef,
} from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { EMPTY, type Observable } from 'rxjs'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { useObservableFocus } from '@/hooks/focus-hooks'
import { isRefreshDesign } from '@/infra/design'
import styles from './SearchField.module.css'

interface SearchFieldProps extends Omit<
  ComponentPropsWithRef<'input'>,
  'onChange' | 'size'
> {
  onChange(newValue: string): unknown

  size?: 'small' | 'normal'

  stretch?: boolean
  focus$?: Observable<unknown>
  showClearButton?: boolean
  embedded?: boolean
  /**
   * The classic design's look; the refresh design renders every appearance
   * the same.
   *
   * - `filled`: grey box with the icon before the input.
   * - `toolbar`: icon and input sit directly on the surrounding surface.
   * - `outlined`: bordered box with the icon after the input.
   * - `plain`: bordered input without an icon or clear button.
   */
  appearance?: 'filled' | 'toolbar' | 'outlined' | 'plain'
  testId?: string
}

/**
 * Search field component, for use in headers.
 */
export function SearchField({
  value,
  className,
  onChange,
  stretch,
  focus$ = EMPTY,
  showClearButton = false,
  testId,
  embedded = false,
  size = 'normal',
  appearance = 'filled',
  ref,
  ...rest
}: SearchFieldProps) {
  const isEmpty = !value
  const inputRef = useRef<HTMLInputElement>(null)
  useObservableFocus(focus$, inputRef)
  const composedRef = useComposedRefs(ref, inputRef)
  const clearSearch = useCallback(() => {
    onChange('')
  }, [onChange])

  const focusInput = useCallback(() => inputRef.current?.focus(), [])

  const change = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse convenience around the input, which is the keyboard target
    <div
      className={cx(
        styles.root,
        stretch && styles.stretch,
        size === 'small' && styles.small,
        embedded && styles.backgroundTransparent,
        !isRefreshDesign && styles[appearance],
        className,
      )}
      onClick={focusInput}
    >
      <span className={styles.searchIcon}>
        <Icon auto name="search" />
      </span>
      <input
        ref={composedRef}
        autoComplete="off"
        type="text"
        placeholder="Search"
        className={cx(styles.input, isEmpty && styles.empty)}
        onChange={change}
        value={value}
        data-test={testId}
        {...rest}
      />

      {!showClearButton && (
        <RawButton
          className={cx(styles.closeIcon, isEmpty && styles.invisible)}
          tabIndex={isEmpty ? -1 : undefined}
          onPress={clearSearch}
        >
          <Icon auto name="close" />
        </RawButton>
      )}

      {showClearButton && (
        <RawButton
          className={cx(styles.clearTextButton, isEmpty && styles.invisible)}
          onPress={clearSearch}
        >
          Clear
        </RawButton>
      )}
    </div>
  )
}
